import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AllRefundOrdersShop from "../Shop/AllRefundOrders";
import AllRefundOrdersBuyer from "./AllRefundOrdersBuyer";
import RefundDetails from "../Shop/RefundDetails";
import { useUnreadNotificationCount } from "../../hooks/useUnreadNotificationCount";
import {
  SELLER_REFUND_REVIEW_TYPES,
  BUYER_REFUND_UPDATE_TYPES,
  VA_MEDIATION_NOTIFICATION_TYPES,
} from "../../utils/notificationTypes";
import {
  isBuyerFacingRefundNotification,
  isSellerFacingRefundNotification,
} from "../../utils/orderNotificationMatch";

const TabBadge = ({ count }) =>
  count > 0 ? (
    <span className="ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white">
      {count > 99 ? "99+" : count}
    </span>
  ) : null;

const DisputesAndRefunds = ({ cardClass }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const buyerFacingPredicate = useCallback(
    (n) => isBuyerFacingRefundNotification(n),
    []
  );
  const sellerFacingPredicate = useCallback(
    (n) => isSellerFacingRefundNotification(n),
    []
  );

  const reviewRefundsUnread = useUnreadNotificationCount(
    [...SELLER_REFUND_REVIEW_TYPES, ...VA_MEDIATION_NOTIFICATION_TYPES],
    sellerFacingPredicate
  );
  const myRefundsUnread = useUnreadNotificationCount(
    [...BUYER_REFUND_UPDATE_TYPES, ...VA_MEDIATION_NOTIFICATION_TYPES],
    buyerFacingPredicate
  );
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab === "review-refunds") return "review-refunds";
    return "my-refunds";
  });

  const orderIdFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("order") || null;
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const newTab = tab === "review-refunds" ? "review-refunds" : "my-refunds";
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.search, activeTab]);

  const handleTabChange = useCallback(
    (tab) => {
      setActiveTab(tab);
      const search = tab === "my-refunds" ? "?tab=my-refunds" : `?tab=${tab}`;
      navigate(`/profile/disputes-refunds${search}`, { replace: true });
    },
    [navigate]
  );

  return (
    <section className={cardClass}>
      <header className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Disputes and Refunds</h2>
        <p className="text-sm text-slate-500">
          Manage refund requests and disputes.
        </p>
        <p className="text-xs text-slate-400 mt-1">
          {activeTab === "review-refunds"
            ? "Buyer Refunds: approve a return, full refund, or partial refund. Only the buyer can cancel an open request from their order page."
            : "My Refunds: your refund requests. Under review = seller is reviewing; Approved = refund granted."}
        </p>
      </header>

      {reviewRefundsUnread > 0 && activeTab !== "review-refunds" && (
        <div
          className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
          role="status"
        >
          <span className="font-semibold">
            {reviewRefundsUnread === 1
              ? "1 refund request waiting for your review."
              : `${reviewRefundsUnread} refund requests waiting for your review.`}
          </span>{" "}
          <button
            type="button"
            onClick={() => handleTabChange("review-refunds")}
            className="font-semibold text-[#38513b] underline underline-offset-2"
          >
            Open Buyer Refunds
          </button>
        </div>
      )}

      {myRefundsUnread > 0 && activeTab === "my-refunds" && (
        <div
          className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-950"
          role="status"
        >
          <p className="font-semibold">
            {myRefundsUnread === 1
              ? "1 refund update on your purchases"
              : `${myRefundsUnread} refund updates on your purchases`}
          </p>
          <p className="mt-1 text-rose-900/90">
            Look for the red <span className="font-semibold">New</span> label next to the Order ID
            below, or open the order for details.
          </p>
        </div>
      )}

      <div className="mb-6 border-b border-slate-200">
        <nav className="flex gap-4">
          <button
            type="button"
            onClick={() => handleTabChange("review-refunds")}
            className={`inline-flex items-center px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === "review-refunds"
                ? "border-b-2 border-[#38513b] text-[#38513b]"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Buyer Refunds
            <TabBadge count={reviewRefundsUnread} />
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("my-refunds")}
            className={`inline-flex items-center px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === "my-refunds"
                ? "border-b-2 border-[#38513b] text-[#38513b]"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            My Refunds
            <TabBadge count={myRefundsUnread} />
          </button>
        </nav>
      </div>

      <div key={activeTab}>
        {activeTab === "review-refunds" ? (
          orderIdFromUrl ? (
            <RefundDetails
              orderIdFromQuery={orderIdFromUrl}
              embedded
              onBackToList={() => navigate("/profile/disputes-refunds?tab=review-refunds")}
            />
          ) : (
            <AllRefundOrdersShop />
          )
        ) : (
          <AllRefundOrdersBuyer cardClass="" />
        )}
      </div>
    </section>
  );
};

export default DisputesAndRefunds;
